import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  formatBRL,
  formatPct,
  formatPeriodShort,
  signedBRL,
  pctChange,
  deltaColor,
} from './diffUtils'
import type {
  CachePeriod,
  DreAuditPeriodChanges,
  DreAuditLineItemChange,
  DreAuditClientRevenueChange,
  DreAuditCategoriaChange,
  DreAuditMatrixClientChange,
  DreAuditMatrixProjectChange,
} from '../../hooks/useDreAudit'

interface AuditDiffTableProps {
  periodChanges: DreAuditPeriodChanges[]
  /** Periods to render (in order). Should match `affectedPeriods` from the snapshot. */
  periods: CachePeriod[]
}

/**
 * Master diff view — one section per affected period.
 * Each period shows four sub-tables stacked vertically:
 *   1. Demonstrativo (lineItems that changed)
 *   2. Receita Bruta por cliente (only changed clients)
 *   3. Despesas Operacionais por categoria (only changed categorias)
 *   4. Matriz Cliente × Projeto (only changed clients with their changed JOBs)
 */
export function AuditDiffTable({ periodChanges, periods }: AuditDiffTableProps) {
  if (periods.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-brand-gray bg-brand-dark-light rounded-monumenta border border-brand-dark-lighter">
        Nenhum período afetado nessa operação.
      </div>
    )
  }

  const byKey = new Map<string, DreAuditPeriodChanges>()
  for (const p of periodChanges) byKey.set(`${p.year}-${p.month}`, p)

  return (
    <div className="space-y-6">
      {periods.map(p => {
        const changes = byKey.get(`${p.year}-${p.month}`)
        return (
          <PeriodSection
            key={`${p.year}-${p.month}`}
            period={p}
            changes={changes}
          />
        )
      })}
    </div>
  )
}

interface PeriodSectionProps {
  period: CachePeriod
  changes: DreAuditPeriodChanges | undefined
}

function PeriodSection({ period, changes }: PeriodSectionProps) {
  return (
    <div className="space-y-4 p-4 bg-brand-dark-light rounded-monumenta border border-brand-dark-lighter">
      <div className="flex items-baseline gap-2">
        <h3 className="text-base font-semibold text-white uppercase tracking-wide">
          {formatPeriodShort(period)}
        </h3>
        <span className="text-xs text-brand-gray">
          {period.month.toString().padStart(2, '0')}/{period.year}
        </span>
      </div>

      <DemonstrativoSection changes={changes?.lineItemChanges ?? []} />
      <ClientsSection changes={changes?.clientRevenueChanges ?? []} />
      <CategoriasSection changes={changes?.categoriaChanges ?? []} />
      <MatrixSection changes={changes?.matrixChanges ?? []} />
    </div>
  )
}

// ===========================================================================
// Sub-tables
// ===========================================================================

function DemonstrativoSection({ changes }: { changes: DreAuditLineItemChange[] }) {
  if (changes.length === 0) {
    return (
      <SectionShell title="Demonstrativo">
        <p className="text-xs text-brand-gray italic">Nenhuma linha do demonstrativo mudou.</p>
      </SectionShell>
    )
  }

  return (
    <SectionShell title="Demonstrativo">
      <div className="overflow-auto rounded-md border border-brand-dark-lighter">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-brand-dark border-b border-brand-dark-lighter">
              <Th>Linha</Th>
              <Th align="right">Antes</Th>
              <Th align="right">Depois</Th>
              <Th align="right">Δ</Th>
              <Th align="right">Δ%</Th>
            </tr>
          </thead>
          <tbody>
            {changes.map(d => {
              const delta = d.after - d.before
              const color = deltaColor(delta)
              return (
                <tr key={d.code} className="border-b border-brand-dark-lighter last:border-0">
                  <td className="px-3 py-1.5 text-white" style={{ paddingLeft: 12 + d.level * 12 }}>
                    {d.label}
                  </td>
                  <td className="px-3 py-1.5 text-right text-brand-gray font-mono">
                    {formatBRL(d.before)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {formatBRL(d.after)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {signedBRL(delta)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {formatPct(pctChange(d.before, d.after))}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionShell>
  )
}

function ClientsSection({ changes }: { changes: DreAuditClientRevenueChange[] }) {
  if (changes.length === 0) {
    return (
      <SectionShell title="Receita Bruta por cliente">
        <p className="text-xs text-brand-gray italic">Nenhum cliente mudou.</p>
      </SectionShell>
    )
  }

  return (
    <SectionShell title="Receita Bruta por cliente">
      <div className="overflow-auto rounded-md border border-brand-dark-lighter">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-brand-dark border-b border-brand-dark-lighter">
              <Th>Cliente</Th>
              <Th align="right">Antes</Th>
              <Th align="right">Depois</Th>
              <Th align="right">Δ</Th>
              <Th align="right">Δ%</Th>
            </tr>
          </thead>
          <tbody>
            {changes.map(d => {
              const delta = d.after - d.before
              const color = deltaColor(delta)
              return (
                <tr
                  key={d.codigoCliente ?? d.nomeCliente}
                  className="border-b border-brand-dark-lighter last:border-0"
                >
                  <td className="px-3 py-1.5 text-white">
                    {d.nomeCliente}
                    {d.codigoCliente && (
                      <span className="text-[10px] text-brand-gray ml-2 font-mono">
                        {d.codigoCliente}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right text-brand-gray font-mono">
                    {formatBRL(d.before)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {formatBRL(d.after)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {signedBRL(delta)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {formatPct(pctChange(d.before, d.after))}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionShell>
  )
}

function CategoriasSection({ changes }: { changes: DreAuditCategoriaChange[] }) {
  if (changes.length === 0) {
    return (
      <SectionShell title="Despesas Operacionais por categoria">
        <p className="text-xs text-brand-gray italic">Nenhuma categoria mudou.</p>
      </SectionShell>
    )
  }

  return (
    <SectionShell title="Despesas Operacionais por categoria">
      <div className="overflow-auto rounded-md border border-brand-dark-lighter">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-brand-dark border-b border-brand-dark-lighter">
              <Th>Categoria</Th>
              <Th align="right">Antes</Th>
              <Th align="right">Depois</Th>
              <Th align="right">Δ</Th>
              <Th align="right">Δ%</Th>
            </tr>
          </thead>
          <tbody>
            {changes.map(d => {
              const delta = d.after - d.before
              const color = deltaColor(delta)
              return (
                <tr
                  key={d.codigoGerencial ?? d.relacaoGerencial}
                  className="border-b border-brand-dark-lighter last:border-0"
                >
                  <td className="px-3 py-1.5 text-white">
                    {d.relacaoGerencial}
                    {d.codigoGerencial && (
                      <span className="text-[10px] text-brand-gray ml-2 font-mono">
                        {d.codigoGerencial}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right text-brand-gray font-mono">
                    {formatBRL(d.before)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {formatBRL(d.after)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {signedBRL(delta)}
                  </td>
                  <td className={`px-3 py-1.5 text-right font-mono ${color}`}>
                    {formatPct(pctChange(d.before, d.after))}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionShell>
  )
}

// ---------------------------------------------------------------------------
// Matriz Cliente × Projeto (expandable per cliente)
// ---------------------------------------------------------------------------
//
// Design notes (audit panel UX revision 2026-05-07):
//   - Margem column dropped — derivable from Δ Receita − Δ Custo, and pushed
//     the table past the viewport on standard laptop widths.
//   - First column ("Cliente / Projeto") is sticky so the user keeps row
//     context while scrolling horizontally through the metric columns.
//   - Cells whose metric block didn't change in a given row render as a muted
//     dash to suppress "R$ 0,00 → R$ 0,00 → +R$ 0,00" noise — keeps the eye
//     on what actually moved.
//   - JOB rows use a tree-prefix ("└─") + deeper indentation so the parent
//     vs child relationship is unmistakable.

const DELTA_THRESHOLD = 0.01

function MatrixSection({ changes }: { changes: DreAuditMatrixClientChange[] }) {
  if (changes.length === 0) {
    return (
      <SectionShell title="Matriz Cliente × Projeto">
        <p className="text-xs text-brand-gray italic">Nenhum cliente teve receita ou custo alterado.</p>
      </SectionShell>
    )
  }

  return (
    <SectionShell title="Matriz Cliente × Projeto">
      <div className="overflow-auto rounded-md border border-brand-dark-lighter">
        <table className="w-full text-xs border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr className="bg-brand-dark">
              <StickyTh>Cliente / Projeto</StickyTh>
              <Th align="right">Receita antes</Th>
              <Th align="right">Receita depois</Th>
              <Th align="right">Δ Receita</Th>
              <Th align="right">Custo antes</Th>
              <Th align="right">Custo depois</Th>
              <Th align="right">Δ Custo</Th>
            </tr>
          </thead>
          <tbody>
            {changes.map(c => (
              <ClientMatrixRow key={c.codigoCliente ?? c.nomeCliente} client={c} />
            ))}
          </tbody>
        </table>
      </div>
    </SectionShell>
  )
}

function ClientMatrixRow({ client }: { client: DreAuditMatrixClientChange }) {
  const [expanded, setExpanded] = useState(false)
  const hasProjects = client.projetos.length > 0

  const receitaDelta = client.receitaAfter - client.receitaBefore
  const custoDelta = client.custoAfter - client.custoBefore
  const receitaChanged = Math.abs(receitaDelta) >= DELTA_THRESHOLD
  const custoChanged = Math.abs(custoDelta) >= DELTA_THRESHOLD

  // Parent rows match the section background so the sticky cell looks seamless.
  const rowBg = 'bg-brand-dark-light'

  return (
    <>
      <tr
        className={`${rowBg} ${hasProjects ? 'cursor-pointer hover:bg-brand-dark/40' : ''}`}
        onClick={() => hasProjects && setExpanded(e => !e)}
      >
        <StickyCell bg={rowBg}>
          <div className="flex items-center gap-1">
            {hasProjects ? (
              expanded
                ? <ChevronDown size={12} className="text-brand-gray shrink-0" />
                : <ChevronRight size={12} className="text-brand-gray shrink-0" />
            ) : (
              <span style={{ width: 12, display: 'inline-block' }} />
            )}
            <span className="text-white">{client.nomeCliente}</span>
            {client.codigoCliente && (
              <span className="text-[10px] text-brand-gray ml-2 font-mono">
                {client.codigoCliente}
              </span>
            )}
          </div>
        </StickyCell>
        <MetricCells before={client.receitaBefore} after={client.receitaAfter} changed={receitaChanged} kind="receita" />
        <MetricCells before={client.custoBefore} after={client.custoAfter} changed={custoChanged} kind="custo" />
      </tr>
      {expanded && client.projetos.map(p => (
        <ProjectMatrixRow key={p.job} project={p} />
      ))}
    </>
  )
}

function ProjectMatrixRow({ project }: { project: DreAuditMatrixProjectChange }) {
  const receitaDelta = project.receitaAfter - project.receitaBefore
  const custoDelta = project.custoAfter - project.custoBefore
  const receitaChanged = Math.abs(receitaDelta) >= DELTA_THRESHOLD
  const custoChanged = Math.abs(custoDelta) >= DELTA_THRESHOLD

  // Project rows render with the same elevated bg so the sticky cell
  // visually inherits and the row isn't confused with a parent.
  const rowBg = 'bg-brand-dark/40'

  return (
    <tr className={rowBg}>
      <StickyCell bg={rowBg}>
        <div className="text-brand-gray text-[11px] flex items-baseline gap-1" style={{ paddingLeft: 28 }}>
          <span className="text-brand-gray/60">└─</span>
          <span className="truncate" title={project.job}>{project.job}</span>
        </div>
      </StickyCell>
      <MetricCells before={project.receitaBefore} after={project.receitaAfter} changed={receitaChanged} kind="receita" small />
      <MetricCells before={project.custoBefore} after={project.custoAfter} changed={custoChanged} kind="custo" small />
    </tr>
  )
}

/**
 * Renders 3 cells (antes / depois / Δ) for one metric block. When `changed`
 * is false, the cells collapse to muted dashes to suppress noise.
 *
 * `kind` decides delta-color polarity: receita up = good (green); custo up =
 * bad (red), so we negate the delta sign when coloring custo cells.
 */
function MetricCells({
  before,
  after,
  changed,
  kind,
  small = false,
}: {
  before: number
  after: number
  changed: boolean
  kind: 'receita' | 'custo'
  small?: boolean
}) {
  const delta = after - before
  const colorDelta = kind === 'custo' ? -delta : delta
  const color = changed ? deltaColor(colorDelta) : 'text-brand-gray/40'
  const sizeCls = small ? 'text-[11px]' : ''

  if (!changed) {
    return (
      <>
        <Td align="right" className={`text-brand-gray/40 font-mono ${sizeCls}`}>—</Td>
        <Td align="right" className={`text-brand-gray/40 font-mono ${sizeCls}`}>—</Td>
        <Td align="right" className={`text-brand-gray/40 font-mono ${sizeCls}`}>—</Td>
      </>
    )
  }

  return (
    <>
      <Td align="right" className={`text-brand-gray font-mono ${sizeCls}`}>{formatBRL(before)}</Td>
      <Td align="right" className={`font-mono ${color} ${sizeCls}`}>{formatBRL(after)}</Td>
      <Td align="right" className={`font-mono ${color} ${sizeCls}`}>{signedBRL(delta)}</Td>
    </>
  )
}

function StickyTh({ children }: { children: React.ReactNode }) {
  return (
    <th
      className="sticky left-0 z-10 bg-brand-dark px-3 py-1.5 text-left text-[10px] font-semibold text-brand-gray uppercase tracking-wide whitespace-nowrap border-b border-brand-dark-lighter"
    >
      {children}
    </th>
  )
}

function StickyCell({ children, bg }: { children: React.ReactNode; bg: string }) {
  return (
    <td className={`sticky left-0 z-[1] ${bg} px-3 py-1.5 border-b border-brand-dark-lighter`}>
      {children}
    </td>
  )
}

function Td({
  children,
  align = 'left',
  className = '',
}: {
  children: React.ReactNode
  align?: 'left' | 'right'
  className?: string
}) {
  return (
    <td className={`px-3 py-1.5 text-${align} border-b border-brand-dark-lighter ${className}`}>
      {children}
    </td>
  )
}

// ===========================================================================
// Tiny shared bits
// ===========================================================================

function SectionShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-brand-gray uppercase tracking-wide">{title}</h4>
      {children}
    </div>
  )
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th
      className={`px-3 py-1.5 text-${align} text-[10px] font-semibold text-brand-gray uppercase tracking-wide whitespace-nowrap`}
    >
      {children}
    </th>
  )
}
